import React, { useState, useCallback, useRef } from 'react';
import { View, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { useTranslation } from '@/hooks/useTranslation';
import { useChatInputStore, useFileStore, useAuthStore, useModalStore } from '@/store';
import useRequireAuth from '@/hooks/useRequireAuth';
import ExplorePage, { ExploreSection } from '@/components/Pages/ExplorePage';
import ImageStyleCarousel, { CarouselNavButtons } from '@/components/ImageStyleCarousel';
import ImageModelsSection from '@/components/ImageModelsSection';
import ImagePromptGrid from '@/components/ImagePromptGrid';
import ImageGallery from '@/components/ImageGallery';
import ToolsByOutputTypeSection from '@/components/Tools/ToolsByOutputTypeSection';
import SectionViewAllLink from '@/components/Explore/SectionViewAllLink';
import AppDashboardFooter from '@/components/AppDashboardFooter';
import { openModelAccessPaywall } from '@/constants/utils/trial';

const ImagesPage = React.memo(() => {
  const router = useRouter();
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [carouselScrollState, setCarouselScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
    showNavButtons: false,
  });
  const carouselRef = useRef(null);

  const initializeImageGeneration = useChatInputStore(state => state.initializeImageGeneration);
  const { fetchGeneratedImages } = useFileStore(state => state.actions);
  const userId = useAuthStore(state => state.user?.id);
  const hasUser = Boolean(userId);
  const { openModal } = useModalStore(state => state.actions);
  const requireAuth = useRequireAuth();

  // Anon visitors sign in first; free users get buy-credits OR Plus paywall.
  const showUpgradeModal = useCallback((model) => {
    const ran = requireAuth(() => true, {
      source: 'images_plus_model_upsell',
      title: 'Sign in to use this model',
      subtitle: 'Premium models need purchased credits or Gab AI Plus on your account.',
      icon: 'image',
    });
    if (ran !== true) return;
    openModelAccessPaywall(openModal, {
      model,
      title: model?.name ? `Unlock ${model.name}` : 'Unlock this model',
      subtitle: 'To use this model, buy credits or upgrade to Plus.',
    });
  }, [openModal, requireAuth]);

  // Search submit kicks off a paid generation — gate anonymous visitors
  // before we route them to `/app` with a `pendingAutoSend` that would
  // silently get dropped by the auto-send `useEffect` (it requires auth).
  const handleSubmit = useCallback(() => {
    if (!inputValue.trim()) return;
    const ran = requireAuth(() => true, {
      source: 'images_search_submit',
      title: 'Sign in to make an image',
      subtitle: 'Image generations are billed to your account so we can apply your credits and save the result.',
      icon: 'image',
    });
    if (ran !== true) return;
    initializeImageGeneration(inputValue.trim(), null, true);
    router.push('/app');
  }, [inputValue, initializeImageGeneration, router, requireAuth]);

  const onRefresh = useCallback(async () => {
    if (!hasUser) return;
    await fetchGeneratedImages(1);
  }, [fetchGeneratedImages, hasUser]);

  return (
    <ExplorePage
      metaTitle="Images"
      metaDescription="Create stunning AI-generated images. Explore different styles, try various image generation models, and manage your image gallery."
      metaCanonical="/images"
      searchPlaceholder={t('images.inputPlaceholder')}
      searchIcon="image"
      searchValue={inputValue}
      onSearchChange={setInputValue}
      onSearchSubmit={handleSubmit}
      onRefresh={onRefresh}
      footer={<AppDashboardFooter fullViewportWidth />}
    >
      <ExploreSection
        title={t('images.tryAStyle')}
        headerRight={
          carouselScrollState.showNavButtons ? (
            <CarouselNavButtons
              canScrollLeft={carouselScrollState.canScrollLeft}
              canScrollRight={carouselScrollState.canScrollRight}
              onScrollLeft={() => carouselRef.current?.scrollBy(-300)}
              onScrollRight={() => carouselRef.current?.scrollBy(300)}
            />
          ) : null
        }
      >
        <ImageStyleCarousel
          ref={carouselRef}
          onScrollStateChange={setCarouselScrollState}
        />
      </ExploreSection>

      {Platform.OS !== 'ios' && (
        <ExploreSection
          title={t('images.tryOurModels')}
          headerRight={<SectionViewAllLink href="/models" label={t('common.viewAllModels')} />}
        >
          <ImageModelsSection onPlusRequired={showUpgradeModal} />
        </ExploreSection>
      )}

      <ExploreSection
        title={t('images.tryOurTools')}
        headerRight={<SectionViewAllLink href="/tools" label={t('common.viewAllTools')} />}
      >
        <ToolsByOutputTypeSection outputType="image" />
      </ExploreSection>

      {/* "Discover something new" prompt grid is hidden for logged-out visitors. */}
      {hasUser && ['web', 'macos'].includes(Platform.OS) && (
        <ExploreSection title={t('images.discoverNew')}>
          <ImagePromptGrid />
        </ExploreSection>
      )}

      {/* Personal gallery is account-bound — hidden for logged-out visitors. */}
      {hasUser && (
        <ExploreSection title={t('images.myImages')}>
          <ImageGallery />
        </ExploreSection>
      )}
    </ExplorePage>
  );
});

function ImagesRoute() {
  return <ImagesPage />;
}

export default React.memo(ImagesRoute);
